import { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/services/api'
import { toast } from 'react-toastify'

interface UploadUrlResponse {
  uploadUrl: string
  s3Key: string
}

const UploadPage = () => {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  
  const queryClient = useQueryClient()

  const createContentMutation = useMutation({
    mutationFn: async (data: { title: string; description?: string }) => {
      const response = await api.post('/contents', data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contents'] })
      toast.success('Contenu créé avec succès')
    },
    onError: () => {
      toast.error('Erreur lors de la création du contenu')
    },
  })

  const getUploadUrlMutation = useMutation({
    mutationFn: async (contentId: number) => {
      const response = await api.post(`/contents/${contentId}/upload-url`, {
        filename: file?.name,
        mimeType: file?.type,
      })
      return response.data as UploadUrlResponse
    },
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!file) {
      toast.error('Veuillez sélectionner un fichier')
      return
    }

    setIsUploading(true)

    try {
      // 1. Créer le contenu
      const content = await createContentMutation.mutateAsync({
        title,
        description,
      })

      // 2. Obtenir l'URL d'upload
      const { uploadUrl, s3Key } = await getUploadUrlMutation.mutateAsync(content.id)

      // 3. Uploader le fichier vers S3
      await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      })

      // 4. Mettre à jour le contenu avec la clé S3
      await api.patch(`/contents/${content.id}`, {
        s3Key,
        mimeType: file.type,
        fileSize: file.size,
      })

      toast.success('Upload terminé avec succès')
      
      // Reset form
      setTitle('')
      setDescription('')
      setFile(null)
    } catch (error) {
      toast.error('Erreur lors de l\'upload')
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <div style={{ maxWidth: '600px', margin: '0 auto' }}>
      <Helmet>
        <title>Upload de contenu - EdenPlaisir.com</title>
        <meta name="description" content="Uploadez votre contenu sur EdenPlaisir.com" />
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      
      <h1>Upload de contenu</h1>
      
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="title">Titre :</label>
          <input
            type="text"
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">Description :</label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
          />
        </div>

        <div className="form-group">
          <label htmlFor="file">Fichier vidéo :</label>
          <input
            type="file"
            id="file"
            accept="video/*"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            required
          />
          {file && (
            <p style={{ fontSize: '0.9rem', color: '#ccc', marginTop: '0.5rem' }}>
              Fichier sélectionné: {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
            </p>
          )}
        </div>

        <button 
          type="submit" 
          className="btn-primary" 
          disabled={isUploading || createContentMutation.isPending}
          style={{ width: '100%' }}
        >
          {isUploading ? 'Upload en cours...' : 'Uploader'}
        </button>
      </form>

      <div className="card" style={{ marginTop: '2rem' }}>
        <h3>Instructions</h3>
        <ul style={{ color: '#ccc' }}>
          <li>Formats supportés: MP4, AVI, MOV, MKV</li>
          <li>Taille maximale: 2GB</li>
          <li>Le contenu sera modéré avant publication</li>
          <li>Vous recevrez une notification une fois approuvé</li>
        </ul>
      </div>
    </div>
  )
}

export default UploadPage
